import { Flex, OverflowText, Pill, PillWrapper, Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { SanitizeFieldValue } from "@/appComponents/Blocks/Cards/RecordInfoCard/utils";
import { AgentStatusPill } from "./AgentStatusPill";
import { AgentHeadingConfig, AgentSlotIds, getAgentSlotFieldName } from "./AgentProfile.constants";

type Props = {
  record: Record<string, any>;
  config: AgentHeadingConfig | null | undefined;
  visibleSlots: Set<string>;
  photoSrc?: string;
};

const AgentPersonChip = ({ label }: { label: string }) => {
  return (
    <div className="oc-agent-profile-heading-person-chip">
      <PillWrapper>
        <Pill label={label} />
      </PillWrapper>
    </div>
  );
};

const PersonChipRow = ({ label, value }: { label: string; value: unknown }) => {
  // Sanitize before rendering: a CRLS-blocked value must show the masked
  // display string, never the raw "__blocked__" sentinel.
  const sanitized = SanitizeFieldValue(value, null, "");
  const chipLabel = typeof sanitized === "string" ? sanitized.trim() : "";
  return (
    <Flex direction="row" alignItems="center" gap="8px">
      <Text size="small" className="oc-font-weight-500 oc-agent-profile-heading-person-label">
        {label}
      </Text>
      {chipLabel ? <AgentPersonChip label={chipLabel} /> : <Text size="small">--</Text>}
    </Flex>
  );
};

export const AgentHeadingProfileCard = ({ record, config, visibleSlots, photoSrc }: Props) => {
  const readSlot = (slotId: (typeof AgentSlotIds)[keyof typeof AgentSlotIds]) => {
    return record?.[getAgentSlotFieldName(config, slotId)];
  };

  const baseClass = "oc-agent-profile-heading";
  const showDepartment = visibleSlots.has(AgentSlotIds.department);
  const showReportsTo = visibleSlots.has(AgentSlotIds.reportsTo);
  const showCreatedBy = visibleSlots.has(AgentSlotIds.createdBy);

  return (
    <Flex direction="column" gap="16px" className={`oc-profile-heading ${baseClass}`}>
      <Flex direction="row" gap="12px" alignItems="flex-start">
        {photoSrc && (
          <div className={`${baseClass}-icon`}>
            <img src={photoSrc} alt="" data-testid={`${baseClass}-photo`} />
          </div>
        )}
        <Flex direction="column" flex="1" className={`${baseClass}-identity`}>
          <Text
            size="xsmall"
            colorModifier="grey"
            className={`${baseClass}-overline`}
            dataTestId={`${baseClass}-type`}
          >
            {SanitizeFieldValue(readSlot(AgentSlotIds.agentType), null, appstr("Blank"))}
          </Text>
          <OverflowText
            size="large"
            className="oc-font-weight-500"
            dataTestId={`${baseClass}-name`}
          >
            {SanitizeFieldValue(readSlot(AgentSlotIds.agentName), null, appstr("Blank"))}
          </OverflowText>
          {showDepartment && (
            <OverflowText size="small" colorModifier="grey" dataTestId={`${baseClass}-department`}>
              {SanitizeFieldValue(readSlot(AgentSlotIds.department), null, appstr("Blank"))}
            </OverflowText>
          )}
        </Flex>
        <AgentStatusPill status={SanitizeFieldValue(readSlot(AgentSlotIds.status), null, "")} />
      </Flex>

      {(showReportsTo || showCreatedBy) && (
        <Flex direction="column" gap="8px">
          {showReportsTo && (
            <PersonChipRow label={appstr("Reports to")} value={readSlot(AgentSlotIds.reportsTo)} />
          )}
          {showCreatedBy && (
            <PersonChipRow label={appstr("Created by")} value={readSlot(AgentSlotIds.createdBy)} />
          )}
        </Flex>
      )}
    </Flex>
  );
};
